/**
 * PhaseDrillIn — panel expandido de la phase seleccionada en el timeline.
 *
 * Muestra TODOS los artifacts del proyecto en esa phase (drafts +
 * in_review + approved + superseded) — no solo oficiales, porque el
 * Lifecycle es donde se TRABAJA. Los row actions cambian según status:
 *
 *   draft (source_mode=template) → "Editar" (Link a /official/$id/edit)
 *   draft (source_mode=upload)   → "Ver" (Link a /official/$id — ahí
 *                                   vive la zona de carga interactiva)
 *   in_review   → "En revisión" (Link a /official/$id, ApprovalPanel)
 *   approved    → "Ver" (Link a /official/$id, viewer read-only)
 *   superseded  → "Ver" (mismo link, render con badge histórico)
 *
 * OT-0064 — el `status` de la fila decide qué acción se ofrece, no si la
 * versión es editable: eso lo confirma `EditVersionLink` contra el servidor
 * al hacer click.
 *
 * OT-0007 — `source_mode` ('template'|'upload') es el eje independiente
 * de `status` que decide QUÉ se renderiza en /official/$id: el editor
 * de secciones o el visor/zona de carga de archivo. Antes esa decisión
 * vivía en `contract_mode='external_canon'` a nivel FASE; ahora es
 * `Artifact.source_mode`, por documento, elegible desde la columna
 * "Modo" de esta tabla (líder o admin).
 *
 * Header con número, nombre, owner, gate description, y botón
 * "Crear artefacto en esta fase" (caso secundario: agregar copias
 * adicionales tipo "Historia de usuario", o artifacts no contemplados
 * por el lifecycle base).
 *
 * Empty states por contract_mode (binario desde OT-0007):
 *  - internal sin artifacts (raro post-B.6.c.1.5: el lifecycle
 *    materializa los iniciales) → "Aún no hay artefactos…"
 *  - skipped → "Phase no contratada…"
 *
 * Badge de fase "Aportado por el cliente": DERIVADO, no declarado — se
 * muestra en el header cuando TODOS los artifacts materializados de la
 * fase están en `source_mode='upload'` (OT-0007 §1 decisión 4).
 *
 * Datos: `GET /api/v1/projects/{id}/artifacts?phase_id=<ProjectPhase.id>` —
 * el backend filtra por la fase del artifact (FK `phase_id`) y devuelve
 * metadata de version actual via JOIN (B.6.c.1.5 backend).
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { Eye, Info, Pencil, Plus } from 'lucide-react'

import {
  getArtifactVersion,
  listProjectArtifacts,
  updateArtifactOwner,
  updateArtifactSourceMode,
  type Artifact,
  type ArtifactSourceMode,
  type ArtifactStatus,
} from '../../api/artifacts'
import { ApiError } from '../../api/client'
import { ConfirmDialog } from '../ConfirmDialog'
import { useMe } from '../../auth/useMe'
import { DisabledActionButton } from '../DisabledActionButton'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import {
  canCreateArtifact,
  canEditArtifact,
  canEditSomeArtifact,
  canManageTeam,
  canOwnArtifact,
  canViewDraft,
  type MaybeAccess,
  type MaybeTier,
  type TeamSection,
} from '../../lib/tiers'
import {
  addableTemplatesQueryKey,
  fetchSnapshotTemplateSummaries,
  updatePhaseOwner,
  type Project,
  type ProjectPhase,
} from '../../api/projects'
import {
  listProjectMembers,
  type ProjectMember,
} from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { isSupportedLocale, toIntlLocale } from '../../i18n/config'
import { pushToast } from '../../stores/toastStore'
import { CreateArtifactDialog } from './official-viewer/CreateArtifactDialog'
import { bySubphaseOrder } from './subphaseOrder'

// La columna "Responsable" del lifecycle ya NO usa mapping intermedio.
// Refactor Plan B: el backend devuelve cada `phase.subphases[]` con
// `role_template_key` + `role_name` ya resueltos al locale del request
// desde el lifecycle correcto del snapshot. El frontend solo cruza
// `role_template_key` con `project_members.role` para resolver "rol →
// persona". Cero replicación de lógica de selección/i18n.

interface PhaseDrillInProps {
  phase: ProjectPhase
  project: Project
  /** Todas las phases del proyecto, ordenadas por phase_order. Se usa
   * para detectar si la phase predecesora no-skipped está aprobada
   * (banner soft de "phase anterior pendiente"). */
  allPhases: ProjectPhase[]
}

export function PhaseDrillIn({ phase, project, allPhases }: PhaseDrillInProps) {
  const { t, i18n } = useTranslation('projects')
  const { accessToken } = useAccessToken()

  // Labels resueltos del backend. `phase.name` viene del lifecycle correcto
  // del snapshot ya con i18n aplicado — sin fallback al phase_code crudo
  // salvo legacy/proyectos pre-Plan B.
  const phaseName = phase.name || phase.phase_code

  // Mapeo subphase_code → nombre humano + role_template_key, ya resuelto
  // por el backend. Se construye una vez por render (idempotente, O(N)).
  const subphaseByCode = useMemo(() => {
    const map = new Map<string, (typeof phase.subphases)[number]>()
    for (const sp of phase.subphases) {
      map.set(sp.subphase_code, sp)
    }
    return map
  }, [phase.subphases])

  const isSkipped = phase.contract_mode === 'skipped'

  const [createOpen, setCreateOpen] = useState(false)

  // Fetch de artifacts de esta phase. Skip cuando phase está skipped
  // (no tiene sentido pedir, esa phase no produce artifacts).
  //
  // `i18n.language` va en el queryKey: el backend resuelve `artifact.name`
  // desde `i18n.{Accept-Language}.name` del template snapshoteado
  // (Doc 19 §3.3.1). El listener global en `main.tsx` invalida estas
  // queries al cambiar idioma; esta dimensión adicional previene que el
  // cache de un idioma sirva al otro tras unmount/remount del drill-in.
  const artifactsQuery = useQuery({
    queryKey: [
      'projects',
      project.id,
      'artifacts',
      'by_phase',
      phase.phase_code,
      i18n.language,
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectArtifacts(accessToken, project.id, {
        phase_id: phase.id,
      })
    },
    enabled: !!accessToken && !isSkipped,
    staleTime: 30_000,
    retry: false,
  })

  const artifacts = artifactsQuery.data ?? []

  // OT-0007 §1 decisión 4 — badge de fase DERIVADO, no declarado: la
  // fase "es" aportada por el cliente cuando TODOS sus documentos
  // materializados están en modo carga. Con 0 artifacts todavía no hay
  // nada que derivar — no se muestra.
  const isClientProvidedPhase =
    artifacts.length > 0 && artifacts.every((a) => a.source_mode === 'upload')

  // Members del proyecto (para resolver "Responsable" → persona cuando
  // existe un miembro que cubre el rol declarado en el lifecycle).
  // `subphase_code → role_template_key` y `role_template_key → role_name`
  // ya vienen resueltos por el backend en `phase.subphases[]`.
  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken && !isSkipped,
    staleTime: 60_000,
    retry: false,
  })

  // OT-0008 §4.4 — el responsable sale de `artifact.owner_user_id`, una
  // persona nombrada. Antes se derivaba cruzando el rol del lifecycle
  // contra un Map de miembros por rol: con dos personas del mismo rol,
  // ese Map conservaba una arbitrariamente y la columna RESPONSABLE
  // mostraba a quien no era.
  //
  // Hubo un intento previo de arreglarlo dando prioridad al líder, pero
  // seguía siendo una heurística: con dos analistas (ninguno líder)
  // volvía a "el primero por created_at". La ambigüedad no se resuelve
  // adivinando mejor — se resuelve no adivinando: el backend deja el
  // dueño sin asignar y el líder decide.
  const { me } = useMe()

  // OT-0009 §4.3 — isMember/isLead se resuelven una sola vez aquí (no
  // por fila) y bajan como prop. Antes `canAssignOwner` hacía este
  // mismo cruce inline; ahora vive en `useProjectAccess`, la única
  // fuente de verdad que consumen también "Crear artefacto en esta
  // fase" y el selector de responsable de la fase (abajo).
  const { isMember, isLead, section } = useProjectAccess(project, membersQuery.data ?? [])

  // Solo admin/editor del equipo operativo pueden ser responsables (D12,
  // OT-0058 §4.5). Filtrar el picker evita ofrecer a alguien que el
  // backend va a rechazar con 422 — pero el enforcement real está allá.
  const ownerCandidates = useMemo(
    () =>
      (membersQuery.data ?? []).filter(
        (m) =>
          m.user_id &&
          m.section === 'operational' &&
          canOwnArtifact(m.user_tier ?? undefined),
      ),
    [membersQuery.data],
  )

  // Asignar responsable (de artifact o de fase) y gestionar el equipo
  // son la misma pregunta de acceso en backend (`can_assign_owner` /
  // `can_manage_team`, cuerpo idéntico) — un solo predicado frontend
  // para las dos. El responsable no se auto-asigna: si no, cualquier
  // miembro tomaría cualquier documento.
  const canAssignOwner = canManageTeam(me?.role, me?.tenant?.access, { isMember, isLead })

  // OT-0009 §1.2 #2 — "Crear artefacto en esta fase" exige, además del
  // estado de la fase, tier de edición y membresía: sin esto, un
  // reviewer o un editor ajeno al proyecto veían el botón y el backend
  // lo rechazaba recién al enviar.
  const canCreateInPhase =
    phase.contract_mode === 'internal' &&
    phase.status !== 'approved' &&
    canCreateArtifact(me?.role, me?.tenant?.access, { isMember, section })

  // OT-0011 — el botón solo se ofrece si el lifecycle declara al menos
  // una plantilla repetible (`user_can_add_more=true`) para esta fase.
  // Gateado también por `canCreateInPhase`: si la fase no admite crear
  // en absoluto, no tiene sentido pedir el dato. Mismo queryKey que
  // `CreateArtifactDialog` (via `addableTemplatesQueryKey`) para que
  // abrir el modal no dispare un segundo fetch.
  const addableTemplatesQuery = useQuery({
    queryKey: addableTemplatesQueryKey(project.id, phase.phase_code),
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchSnapshotTemplateSummaries(accessToken, project.id, {
        template_type: 'artifact',
        addable_in_phase: phase.phase_code,
      })
    },
    enabled: !!accessToken && canCreateInPhase,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
  })

  // Mientras carga, tampoco se pinta el botón — evita el parpadeo de una
  // acción que puede desaparecer un instante después.
  const showCreateButton =
    canCreateInPhase &&
    !addableTemplatesQuery.isLoading &&
    (addableTemplatesQuery.data?.length ?? 0) > 0

  const memberByUserId = useMemo(() => {
    const map = new Map<string, ProjectMember>()
    for (const m of membersQuery.data ?? []) {
      if (m.user_id) map.set(m.user_id, m)
    }
    return map
  }, [membersQuery.data])

  // OT-0009 M2 — gatear la acción no debe ocultar el dato (§4.1). Mismo
  // patrón que `personLabel` a nivel de artifact: sin permiso para
  // reasignar, mostrar quién es el responsable en vez de hacer
  // desaparecer el bloque entero.
  const phaseOwnerMember = phase.owner_user_id
    ? memberByUserId.get(phase.owner_user_id)
    : undefined
  const phaseOwnerLabel =
    phaseOwnerMember?.user_display_name ?? phaseOwnerMember?.user_email ?? null

  // ─── Detección de predecesora no aprobada ──────────────────────────────
  // Filosofía (decisión de producto, ver Doc 08):
  //  - NO bloqueamos la edición de drafts: la IA puede asistir usando
  //    drafts in-progress de phases anteriores, marcados claramente
  //    como contexto preliminar.
  //  - Cuando la phase predecesora cierre (oficial), Change Impact
  //    Analysis (Fase D) detectará si los drafts de phases siguientes
  //    necesitan revisión.
  //  - Mientras tanto, banner soft que setea expectativas con el user.
  // Buscamos la phase no-skipped inmediatamente anterior a la actual.
  const previousActivePhase = useMemo(() => {
    if (isSkipped) return null
    const ordered = [...allPhases].sort(
      (a, b) => a.phase_order - b.phase_order,
    )
    const idx = ordered.findIndex((p) => p.phase_code === phase.phase_code)
    if (idx <= 0) return null
    for (let i = idx - 1; i >= 0; i--) {
      const candidate = ordered[i]
      if (candidate && candidate.contract_mode !== 'skipped') {
        return candidate
      }
    }
    return null
  }, [allPhases, phase.phase_code, isSkipped])

  const showPredecessorBanner =
    previousActivePhase !== null &&
    previousActivePhase.status !== 'approved'

  const previousPhaseName = previousActivePhase
    ? previousActivePhase.name || previousActivePhase.phase_code
    : ''

  const numberCircleClass = isSkipped
    ? 'w-8 h-8 rounded-full bg-surface-muted text-ink-subtle grid place-items-center font-display text-xs font-semibold shrink-0'
    : phase.status === 'approved'
      ? 'w-8 h-8 rounded-full bg-official text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
      : phase.status === 'in_progress'
        ? 'w-8 h-8 rounded-full bg-review text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
        : phase.status === 'blocked'
          ? 'w-8 h-8 rounded-full bg-critical text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
          : 'w-8 h-8 rounded-full bg-canvas border-2 border-line-strong text-ink-muted grid place-items-center font-display text-xs font-semibold shrink-0'

  return (
    <section className="mt-8 bg-surface border border-line rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-line bg-surface-muted flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={numberCircleClass}>{phase.phase_order}</div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
              {t('lifecycle.drill_in.expanded_eyebrow', {
                order: phase.phase_order,
              })}
            </p>
            <h3 className="font-display text-lg font-semibold text-ink truncate">
              {phaseName}
              {isClientProvidedPhase ? (
                <span className="ml-2 align-middle rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ink-subtle">
                  {t('lifecycle.drill_in.phase_client_provided_badge')}
                </span>
              ) : null}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap">
          {/* OT-0009 §1.2 #3 — antes se pintaba para cualquiera; el
              backend (`PATCH /projects/{id}/phases/{id}`) exige
              admin∪líder, exactamente lo que ya calcula `canAssignOwner`
              para el picker de responsable de artifact de abajo. */}
          {canAssignOwner ? (
            <PhaseOwnerSelect
              phase={phase}
              projectId={project.id}
              internalMembers={(membersQuery.data ?? []).filter(
                (m) =>
                  m.user_id !== null &&
                  m.section === 'operational' &&
                  canOwnArtifact(m.user_tier),
              )}
            />
          ) : phaseOwnerLabel ? (
            <span className="text-ink-muted">
              {t('team.phase_owner.label')}:{' '}
              <span className="text-ink font-medium">{phaseOwnerLabel}</span>
            </span>
          ) : null}
          <span className="text-ink-muted">
            {t('lifecycle.drill_in.gate_label')}:{' '}
            <span
              className={
                phase.status === 'in_progress'
                  ? 'text-review font-medium'
                  : 'text-ink font-medium'
              }
            >
              {phase.gate_description ??
                t('lifecycle.drill_in.gate_default')}
            </span>
          </span>
          {showCreateButton ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 font-ui text-xs text-ink hover:border-line-strong hover:bg-surface transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('official_editor.trigger.label_for_phase')}
            </button>
          ) : null}
        </div>
      </div>

      {/* Banner soft: phase predecesora no aprobada. Aplica solo en
          modo internal (skipped no aplica; client-provided no tiene
          dependencia rígida del orden). */}
      {showPredecessorBanner && phase.contract_mode === 'internal' ? (
        <div className="mx-6 mt-4 mb-2 flex items-start gap-3 rounded-md border border-review/30 bg-review-soft/40 px-4 py-3 text-xs text-ink">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-review" />
          <p className="font-body leading-relaxed">
            {t('lifecycle.drill_in.predecessor_pending', {
              previousPhaseName,
            })}
          </p>
        </div>
      ) : null}

      {/* Body */}
      {isSkipped ? (
        <EmptyState mode="skipped" />
      ) : artifactsQuery.isLoading ? (
        <p className="px-6 py-6 font-mono text-xs text-ink-subtle">
          {t('lifecycle.loading')}
        </p>
      ) : artifacts.length === 0 ? (
        <EmptyState mode="internal" />
      ) : (
        <ArtifactTable
          artifacts={artifacts}
          projectId={project.id}
          subphaseByCode={subphaseByCode}
          memberByUserId={memberByUserId}
          ownerCandidates={ownerCandidates}
          canAssignOwner={canAssignOwner}
          role={me?.role}
          access={me?.tenant?.access}
          isMember={isMember}
          isLead={isLead}
          section={section}
          meId={me?.id}
        />
      )}

      {createOpen ? (
        <CreateArtifactDialog
          onClose={() => setCreateOpen(false)}
          projectId={project.id}
          contentLanguage={project.content_language === 'en' ? 'en' : 'es'}
          phaseCode={phase.phase_code}
        />
      ) : null}
    </section>
  )
}

// ─── Tabla de artifacts ────────────────────────────────────────────────────
function ArtifactTable({
  artifacts,
  projectId,
  subphaseByCode,
  memberByUserId,
  ownerCandidates,
  canAssignOwner,
  role,
  access,
  isMember,
  isLead,
  section,
  meId,
}: {
  artifacts: Artifact[]
  projectId: string
  /** Map `subphase_code → ProjectSubphase` con name + role ya resueltos
   * por el backend. La key viene de `phase.subphases[]`. */
  subphaseByCode: Map<string, ProjectPhase['subphases'][number]>
  memberByUserId: Map<string, ProjectMember>
  ownerCandidates: ProjectMember[]
  canAssignOwner: boolean
  /** Contexto de acceso (OT-0009 §4.3), resuelto una vez arriba — nunca
   * por fila. `isOwner` sí es por fila, se calcula en `ArtifactRow`. */
  role: MaybeTier
  access: MaybeAccess
  isMember: boolean
  isLead: boolean
  /** OT-0058 §4.5 — sección del usuario en el proyecto (`null` si no es miembro). */
  section: TeamSection | null
  meId: string | null | undefined
}) {
  const { t } = useTranslation('projects')
  // Orden de las subfases del ciclo, no del texto del código (OT-0006).
  const sorted = useMemo(
    () => [...artifacts].sort(bySubphaseOrder([...subphaseByCode.values()])),
    [artifacts, subphaseByCode],
  )

  return (
    <table className="w-full text-sm">
      <thead className="text-ink-muted text-[11px] uppercase tracking-wider border-b border-line">
        <tr>
          <th className="text-left font-medium px-6 py-2.5">
            {t('lifecycle.drill_in.table.subphase')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.artifact')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.responsible')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.source_mode')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.status')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.version')}
          </th>
          <th className="text-left font-medium px-3 py-2.5">
            {t('lifecycle.drill_in.table.updated')}
          </th>
          <th className="px-3 py-2.5"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {sorted.map((artifact) => {
          const sub = artifact.subphase_code
            ? subphaseByCode.get(artifact.subphase_code) ?? null
            : null
          return (
            <ArtifactRow
              key={artifact.id}
              artifact={artifact}
              projectId={projectId}
              memberByUserId={memberByUserId}
              ownerCandidates={ownerCandidates}
              canAssignOwner={canAssignOwner}
              role={role}
              access={access}
              isMember={isMember}
              isLead={isLead}
              section={section}
              meId={meId}
              subphaseName={sub?.name ?? null}
              roleTemplateKey={sub?.role_template_key ?? null}
              roleName={sub?.role_name ?? null}
            />
          )
        })}
      </tbody>
    </table>
  )
}

function ArtifactRow({
  artifact,
  projectId,
  memberByUserId,
  ownerCandidates,
  canAssignOwner,
  role,
  access,
  isMember,
  isLead,
  section,
  meId,
  subphaseName,
  roleTemplateKey,
  roleName,
}: {
  artifact: Artifact
  projectId: string
  memberByUserId: Map<string, ProjectMember>
  ownerCandidates: ProjectMember[]
  canAssignOwner: boolean
  role: MaybeTier
  access: MaybeAccess
  isMember: boolean
  isLead: boolean
  /** OT-0058 §4.5 — sección del usuario en el proyecto (`null` si no es miembro). */
  section: TeamSection | null
  meId: string | null | undefined
  /** Nombre humano de la sub-fase, ya resuelto por el backend. `null`
   * si el artifact no tiene `subphase_code` o la sub-fase no está en el
   * lifecycle (caso edge: artifact huérfano). */
  subphaseName: string | null
  /** `template_key` del rol responsable declarado por el lifecycle
   * (ej. 'rol-ba'). El backend lo resolvió del lifecycle correcto del
   * snapshot. Cruza con `project_members.role` para identificar persona. */
  roleTemplateKey: string | null
  /** Display name del rol ya resuelto al locale del request. */
  roleName: string | null
}) {
  const { t } = useTranslation('projects')
  const isInReview = artifact.status === 'in_review'
  const rowClass = isInReview
    ? 'bg-review-soft/40 hover:bg-review-soft/70'
    : 'hover:bg-surface-muted'

  // ─── Resolución de responsable (OT-0008 §4.4) ────────────────────────────
  // Lectura directa de `artifact.owner_user_id`: una persona nombrada,
  // no una inferencia. El `roleTemplateKey` del lifecycle sigue aquí
  // pero ya solo como CONTEXTO — dice qué rol se espera para este
  // entregable, no quién responde por él.
  const ownerMember = artifact.owner_user_id
    ? memberByUserId.get(artifact.owner_user_id)
    : undefined
  const personLabel =
    ownerMember?.user_display_name ?? ownerMember?.user_email ?? null

  const lastEditor = artifact.current_version_updated_by_user_id
    ? memberByUserId.get(artifact.current_version_updated_by_user_id)
    : undefined
  const lastEditorLabel = lastEditor?.user_display_name ?? lastEditor?.user_email ?? null

  // OT-0009 §1.2 #1 — "Editar" solo se ofrece a dueño∪líder∪admin
  // (D11). `editingTierAtAll` separa el caso "tu tier no edita nunca"
  // (oculto) del caso "tu tier edita, pero no este documento"
  // (deshabilitado + tooltip) — mismo criterio para el link normal y
  // para "Editar borrador" de una draft post-canon.
  const editingTierAtAll = canEditSomeArtifact(role, access)
  const canEditThisArtifact = canEditArtifact(role, access, {
    isMember,
    isLead,
    section,
    isOwner: artifact.owner_user_id === meId,
  })
  // OT-0009 M-A — no poder editar no es no poder leer. El predicado
  // vive en `lib/tiers.ts` con la correspondencia al backend documentada.
  const canViewThisDraft = canViewDraft(role)

  let responsibleNode: React.ReactNode
  if (canAssignOwner) {
    // Líder o admin: puede reasignar in-place. El rol esperado queda
    // debajo como contexto de a quién buscar.
    responsibleNode = (
      <div className="flex flex-col gap-1">
        <ArtifactOwnerSelect
          artifact={artifact}
          projectId={projectId}
          candidates={ownerCandidates}
        />
        {roleName ? (
          <span className="text-[10px] text-ink-subtle">{roleName}</span>
        ) : null}
      </div>
    )
  } else if (personLabel) {
    responsibleNode = (
      <div className="flex flex-col">
        <span className="text-ink">{personLabel}</span>
        {roleName ? (
          <span className="text-[10px] text-ink-subtle">{roleName}</span>
        ) : null}
      </div>
    )
  } else if (roleTemplateKey) {
    // Sin dueño asignado. Con 0 o 2+ candidatos del rol, el backend deja
    // esto en blanco a propósito (D10) y el líder decide: mostrar el rol
    // esperado le dice a quién buscar.
    responsibleNode = (
      <div className="flex flex-col">
        <span className="text-ink-muted">{roleName ?? roleTemplateKey}</span>
        <span className="text-[10px] text-ink-subtle italic">
          {t('lifecycle.drill_in.row.role_unassigned')}
        </span>
      </div>
    )
  } else {
    responsibleNode = <span className="text-ink-subtle">—</span>
  }

  return (
    <tr className={rowClass}>
      <td className="px-6 py-3">
        {artifact.subphase_code ? (
          <div className="space-y-0.5">
            <div className="text-sm text-ink">
              {subphaseName ?? artifact.subphase_code}
            </div>
            <div className="font-mono text-[10px] text-ink-subtle">
              {artifact.subphase_code}
            </div>
          </div>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-ink">{artifact.name}</div>
        {artifact.code ? (
          <div className="text-[10px] font-mono text-ink-subtle">
            {artifact.code}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-3 text-xs">{responsibleNode}</td>
      <td className="px-3 py-3">
        {canAssignOwner ? (
          <ArtifactSourceModeSelect
            artifact={artifact}
            projectId={projectId}
          />
        ) : (
          <SourceModeBadge sourceMode={artifact.source_mode} />
        )}
      </td>
      <td className="px-3 py-3">
        <ArtifactStatusBadge status={artifact.status} />
      </td>
      <td className="px-3 py-3 text-ink-muted">
        {renderVersion(artifact, t)}
      </td>
      <td className="px-3 py-3 text-ink-muted text-xs">
        {/* Fecha de la VERSIÓN + quién la tocó (D15). Si no hay editor
            registrado —versiones anteriores a la OT-0008— se muestra
            solo la fecha en vez de inventar un nombre. */}
        {formatDate(artifact.current_version_updated_at ?? artifact.updated_at)}
        {lastEditorLabel ? (
          <span className="text-ink-subtle"> · {lastEditorLabel}</span>
        ) : null}
      </td>
      <td className="px-3 py-3 text-right">
        <RowAction
          artifact={artifact}
          projectId={projectId}
          editingTierAtAll={editingTierAtAll}
          canEditThisArtifact={canEditThisArtifact}
          canViewDraft={canViewThisDraft}
        />
      </td>
    </tr>
  )
}

function ArtifactStatusBadge({ status }: { status: ArtifactStatus }) {
  const { t } = useTranslation('projects')
  const className =
    status === 'approved'
      ? 'bg-official-soft text-official'
      : status === 'in_review'
        ? 'bg-review-soft text-review'
        : status === 'superseded'
          ? 'bg-surface-muted text-ink-subtle line-through'
          : // 'external_canon' is the literal API enum value; UI label uses client_provided.
            status === 'external_canon'
            ? 'bg-surface-muted text-ink-subtle border border-line'
            : 'bg-surface-muted text-ink-muted'
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${className}`}
    >
      {t(`lifecycle.artifact_status.${status}` as 'lifecycle.artifact_status.draft')}
    </span>
  )
}

// ─── Modo de producción (OT-0007 §4.1/§4.4/§4.5) ───────────────────────────
function SourceModeBadge({ sourceMode }: { sourceMode: ArtifactSourceMode }) {
  const { t } = useTranslation('projects')
  const className =
    sourceMode === 'upload'
      ? 'bg-review-soft text-review'
      : 'bg-brand-soft text-brand'
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${className}`}
    >
      {t(`lifecycle.drill_in.source_mode.${sourceMode}`)}
    </span>
  )
}

/**
 * Selector plantilla/carga por artefacto (OT-0007 §4.5) — del **líder
 * del proyecto o del admin**, mismo criterio y mismo componente padre
 * que ya gatea `ArtifactOwnerSelect` (`canAssignOwner`).
 *
 * Editable mientras haya un draft mutable: el artifact en `draft` (v1
 * inicial) o un v(N+1) post-canon abierto (`active_draft_version_id`) —
 * mismo criterio que el backend valida contra la versión target (409
 * `ARTIFACT_SOURCE_MODE_LOCKED` en otro caso). Sin draft de ningún tipo
 * (solo canon vigente), bloqueado hasta abrir una versión nueva.
 *
 * El cambio exige confirmación explícita (§4.4). El backend descarta el
 * eje contrario al cambiar: a `upload` vacía el `content` redactado, y a
 * `template` BORRA el archivo del storage. Ninguna de las dos es
 * reversible, así que no puede dispararse desde el `onChange` del select.
 *
 * Se confirma en ambos sentidos y no solo cuando hay algo que perder: el
 * DTO de la fila no trae `content`/`blob_refs` para saberlo, y además el
 * cambio de modo ya es consecuente por sí mismo (§4.5 — un documento
 * cargado pierde estructura de secciones, redacción asistida, y las
 * anclas por `section_id` que el RAG y el Análisis de Impacto recorren).
 */
function ArtifactSourceModeSelect({
  artifact,
  projectId,
}: {
  artifact: Artifact
  projectId: string
}) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  // Modo elegido en el select, a la espera de confirmación. El select
  // sigue controlado por `artifact.source_mode`, así que cancelar lo
  // revierte solo — sin estado espejo que pueda desincronizarse.
  const [pendingMode, setPendingMode] = useState<ArtifactSourceMode | null>(null)

  const isLocked = artifact.status !== 'draft' && !artifact.active_draft_version_id

  const mutation = useMutation({
    mutationFn: async (mode: ArtifactSourceMode) => {
      if (!accessToken) throw new Error('no_access_token')
      return updateArtifactSourceMode(
        accessToken,
        artifact.id,
        mode,
        artifact.active_draft_version_id,
      )
    },
    onSuccess: () => {
      setPendingMode(null)
      pushToast({
        kind: 'success',
        message: t('lifecycle.drill_in.source_mode.toast_success'),
      })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'artifacts'],
      })
    },
    onError: (err) => {
      setPendingMode(null)
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('lifecycle.drill_in.source_mode.toast_failed')
      pushToast({
        kind: 'critical',
        message: code
          ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
          : fallback,
        duration: 6000,
      })
    },
  })

  if (isLocked) {
    return (
      <span title={t('lifecycle.drill_in.source_mode.locked_tooltip')}>
        <SourceModeBadge sourceMode={artifact.source_mode} />
      </span>
    )
  }

  return (
    <>
      <select
        value={artifact.source_mode}
        onChange={(e) =>
          setPendingMode(e.target.value as ArtifactSourceMode)
        }
        disabled={mutation.isPending}
        aria-label={t('lifecycle.drill_in.source_mode.select_aria', {
          name: artifact.name,
        })}
        className="bg-canvas border border-line rounded-md px-2 py-1 text-xs text-ink focus:outline-none focus:border-brand disabled:opacity-50"
      >
        <option value="template">
          {t('lifecycle.drill_in.source_mode.template')}
        </option>
        <option value="upload">
          {t('lifecycle.drill_in.source_mode.upload')}
        </option>
      </select>

      <ConfirmDialog
        open={pendingMode !== null}
        tone="danger"
        title={
          pendingMode === 'upload'
            ? t('lifecycle.drill_in.source_mode.confirm_to_upload_title')
            : t('lifecycle.drill_in.source_mode.confirm_to_template_title')
        }
        body={
          pendingMode === 'upload'
            ? t('lifecycle.drill_in.source_mode.confirm_to_upload_body', {
                name: artifact.name,
              })
            : t('lifecycle.drill_in.source_mode.confirm_to_template_body', {
                name: artifact.name,
              })
        }
        confirmLabel={t('lifecycle.drill_in.source_mode.confirm_cta')}
        cancelLabel={t('lifecycle.drill_in.source_mode.confirm_cancel')}
        busy={mutation.isPending}
        onCancel={() => setPendingMode(null)}
        onConfirm={() => {
          if (pendingMode) mutation.mutate(pendingMode)
        }}
      />
    </>
  )
}

function RowAction({
  artifact,
  projectId,
  editingTierAtAll,
  canEditThisArtifact,
  canViewDraft,
}: {
  artifact: Artifact
  projectId: string
  /** `false` = el tier de este usuario no edita NUNCA (reviewer/external
   *  — D12). El control de editar no existe para su rol: se oculta. */
  editingTierAtAll: boolean
  /** `true` = dueño∪líder∪admin sobre ESTE artifact concreto (D11). */
  canEditThisArtifact: boolean
  /** `true` = tier interno, que puede LEER un borrador aunque no lo
   *  edite. `external` queda fuera (M-A). */
  canViewDraft: boolean
}) {
  const { t } = useTranslation('projects')

  // approved + is_canon (official) → Link al viewer.
  // superseded → Link al viewer (con badge histórico).
  if (
    (artifact.status === 'approved' && artifact.is_canon) ||
    artifact.status === 'superseded'
  ) {
    // Doc 07 — Si hay una draft post-canon en progreso, exponer
    // acción doble: "Editar borrador" + "Ver oficial". Sin esto el
    // user que abrió una v(N+1) no encuentra cómo volver a editarla.
    // OT-0009 §1.2 #1 — "Editar borrador" es la misma acción que
    // "Editar" sobre un draft normal: mismo gate, mismo criterio.
    if (artifact.active_draft_version_id) {
      return (
        <div className="flex items-center gap-2">
          {editingTierAtAll ? (
            canEditThisArtifact ? (
              <EditVersionLink
                projectId={projectId}
                artifactId={artifact.id}
                versionId={artifact.active_draft_version_id}
                search={{ v: artifact.active_draft_version_id }}
                className="text-xs text-review hover:underline inline-flex items-center gap-1"
                title={
                  artifact.active_draft_change_reason ??
                  t('lifecycle.drill_in.row.edit_draft_tooltip')
                }
              >
                <Pencil className="w-3.5 h-3.5" />
                {t('lifecycle.drill_in.row.edit_draft', {
                  n: artifact.active_draft_version_number ?? '?',
                })}
              </EditVersionLink>
            ) : (
              <DisabledActionButton
                title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
              >
                <Pencil className="w-3.5 h-3.5" />
                {t('lifecycle.drill_in.row.edit_draft', {
                  n: artifact.active_draft_version_number ?? '?',
                })}
              </DisabledActionButton>
            )
          ) : null}
          <Link
            to="/projects/$projectId/official/$artifactId"
            params={{ projectId, artifactId: artifact.id }}
            className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1"
          >
            <Eye className="w-3.5 h-3.5" />
            {t('lifecycle.drill_in.row.view')}
          </Link>
        </div>
      )
    }
    return (
      <Link
        to="/projects/$projectId/official/$artifactId"
        params={{ projectId, artifactId: artifact.id }}
        className="text-xs text-brand hover:underline inline-flex items-center gap-1"
      >
        <Eye className="w-3.5 h-3.5" />
        {t('lifecycle.drill_in.row.view')}
      </Link>
    )
  }

  // draft → "Editar" (cuando procede) + "Ver". OT-0009 §1.2 #1: el caso
  // más caro de la OT — un editor que no es dueño ni líder veía
  // "Editar", escribía, y perdía el trabajo al guardar (403
  // FORBIDDEN_NOT_OWNER).
  if (artifact.status === 'draft') {
    // OT-0009 M-A — el control de editar se resuelve como en la fila de
    // una draft post-canon (oculto / deshabilitado / activo), pero la
    // lectura del borrador queda siempre disponible para el tier interno
    // (ver `canViewDraft`): sin este link, un reviewer no tiene por dónde
    // abrir el documento que sí puede comentar.
    if (!canViewDraft) return null

    // OT-0007 — en modo carga no hay secciones que editar: la zona de
    // carga vive en `/official/$id`, que decide el render según
    // `source_mode`. Va DESPUÉS del gate de `canViewDraft`, no antes:
    // el tier que no puede leer un borrador tampoco puede leer este.
    if (artifact.source_mode === 'upload') {
      return (
        <Link
          to="/projects/$projectId/official/$artifactId"
          params={{ projectId, artifactId: artifact.id }}
          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
        >
          <Eye className="w-3.5 h-3.5" />
          {t('lifecycle.drill_in.row.view')}
        </Link>
      )
    }

    // draft en modo plantilla → editor de secciones.
    return (
      <div className="flex items-center gap-2">
        {editingTierAtAll ? (
          canEditThisArtifact ? (
            <EditVersionLink
              projectId={projectId}
              artifactId={artifact.id}
              versionId={artifact.current_version_id}
              search={{ v: undefined }}
              className="text-xs text-brand hover:underline inline-flex items-center gap-1"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('lifecycle.drill_in.row.edit')}
            </EditVersionLink>
          ) : (
            <DisabledActionButton
              title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('lifecycle.drill_in.row.edit')}
            </DisabledActionButton>
          )
        ) : null}
        {/* "Ver" es la acción primaria cuando el usuario no puede editar
            —es la única que le queda— y secundaria cuando acompaña a un
            "Editar" activo, igual que en la fila de la draft post-canon. */}
        <Link
          to="/projects/$projectId/official/$artifactId"
          params={{ projectId, artifactId: artifact.id }}
          className={
            canEditThisArtifact
              ? 'text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1'
              : 'text-xs text-brand hover:underline inline-flex items-center gap-1'
          }
        >
          <Eye className="w-3.5 h-3.5" />
          {t('lifecycle.drill_in.row.view')}
        </Link>
      </div>
    )
  }

  // in_review → Link al viewer. El ApprovalPanel del viewer pinta los
  // botones Aprobar / Rechazar para los aprobadores del stage activo
  // (Doc 21 Fase 2.c). Antes era disabled con TODO; ahora que el
  // ApprovalPanel existe, el LP necesita este link para abrir el
  // documento y firmar.
  if (artifact.status === 'in_review') {
    return (
      <Link
        to="/projects/$projectId/official/$artifactId"
        params={{ projectId, artifactId: artifact.id }}
        className="text-xs text-review hover:underline inline-flex items-center gap-1"
      >
        <Eye className="w-3.5 h-3.5" />
        {t('lifecycle.drill_in.row.review')}
      </Link>
    )
  }

  // Fallback defensivo: status sin acción definida (ej. `cancelled`, o
  // el enum legacy `external_canon` — OT-0007 confirmó 0 escrituras
  // reales fuera del propio enum).
  return (
    <button
      type="button"
      disabled
      className="text-xs text-ink-subtle disabled:cursor-not-allowed"
    >
      {t('lifecycle.drill_in.row.view')}
    </button>
  )
}

/**
 * "Editar" de una fila — OT-0064. La fila viene del listado (`ArtifactRead`),
 * que no trae la editabilidad de sus versiones: el `status` sólo dice qué
 * versión se abriría (la v1 vigente o la draft post-canon) y puede llevar
 * hasta `staleTime` de atraso. La decisión la toma el servidor con
 * `ArtifactVersion.is_editable`, pedida al hacer click: una sola consulta
 * por intención de editar —no una por fila— y con la misma query key que el
 * editor, así el resultado queda en la caché de esa versión.
 *
 * Si la versión ya no es editable no se navega: se refresca el listado (la
 * fila pasa a "En revisión"/"Ver") y se avisa. Si la consulta falla tampoco:
 * sin veredicto no se abre el editor. Abrir en otra pestaña no pasa por
 * aquí, y el editor aplica la misma regla al cargar.
 */
function EditVersionLink({
  projectId,
  artifactId,
  versionId,
  search,
  className,
  title,
  children,
}: {
  projectId: string
  artifactId: string
  versionId: string | null
  search: { v: string | undefined }
  className: string
  title?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation('projects')
  const { t: tErrors } = useTranslation('errors')
  const apiErrorMessage = useApiErrorMessage()
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [checking, setChecking] = useState(false)

  const linkProps = {
    to: '/projects/$projectId/official/$artifactId/edit',
    params: { projectId, artifactId },
    search,
  } as const

  async function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // Click modificado (pestaña nueva): lo resuelve el navegador.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    if (checking || !accessToken || !versionId) return
    setChecking(true)
    try {
      const version = await queryClient.fetchQuery({
        queryKey: ['artifacts', artifactId, 'versions', versionId],
        queryFn: () => getArtifactVersion(accessToken, artifactId, versionId),
        staleTime: 0,
      })
      if (version.is_editable) {
        void navigate(linkProps)
        return
      }
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'artifacts'] })
      pushToast({
        kind: 'review',
        message: t('lifecycle.drill_in.row.edit_unavailable_toast'),
        duration: 6000,
      })
    } catch (err) {
      // Sin veredicto del servidor (red, 404, 403…) no hay base para abrir
      // el editor: se queda en la lista y se dice por qué.
      pushToast({
        kind: 'critical',
        message: err instanceof ApiError ? apiErrorMessage(err) : tErrors('UNKNOWN'),
        duration: 6000,
      })
    } finally {
      setChecking(false)
    }
  }

  return (
    <Link
      {...linkProps}
      className={className}
      title={title}
      aria-busy={checking}
      onClick={(e) => void handleClick(e)}
    >
      {children}
    </Link>
  )
}

function renderVersion(
  artifact: Artifact,
  t: ReturnType<typeof useTranslation<'projects'>>['t'],
): string {
  const n = artifact.current_version_number
  if (artifact.is_canon && n) {
    return t('lifecycle.version_official', { n })
  }
  if (artifact.status === 'superseded' && n) {
    return t('lifecycle.drill_in.row_status.superseded', { n })
  }
  // Drafts e in_review todavía no son oficiales, pero la versión existe
  // (cada artifact materializado arranca en v1). Mostrar el número ayuda
  // al usuario a entender que su edición se está versionando.
  if (artifact.status === 'draft' && n) {
    return t('lifecycle.version_draft', { n })
  }
  if (artifact.status === 'in_review' && n) {
    return t('lifecycle.version_in_review', { n })
  }
  return t('lifecycle.version_pending')
}

// ─── Empty state según contract_mode (binario desde OT-0007) ───────────────
function EmptyState({ mode }: { mode: 'internal' | 'skipped' }) {
  const { t } = useTranslation('projects')
  const key =
    mode === 'skipped'
      ? 'lifecycle.drill_in.empty_skipped'
      : 'lifecycle.drill_in.empty_internal'
  return (
    <div className="px-6 py-10 text-center">
      <p className="font-body text-sm text-ink-muted max-w-prose mx-auto">
        {t(key as 'lifecycle.drill_in.empty_internal')}
      </p>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatDate(iso: string): string {
  const locale = toIntlLocale(
    isSupportedLocale(i18n.language) ? i18n.language : 'es',
  )
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

// ─── Phase owner selector (Doc 26 §2.4 Commit 5) ──────────────────────────
/**
 * Dropdown editable del owner de la phase. Permite:
 *  · Asignar como owner a cualquier member INTERNAL del proyecto
 *    (incluyendo el lead — el caller pre-filtra por scope=internal
 *    + user_id != null para excluir stakeholders sin cuenta).
 *  · Limpiar el override seleccionando `(Sin responsable)` → manda
 *    `owner_user_id=null` al backend, que dispara el fallback a
 *    `lead_member.user_id` en futuras lecturas.
 *
 * El backend valida que el `owner_user_id` (si no es NULL) sea miembro
 * internal del proyecto, y devuelve 409 PHASE_OWNER_NOT_PROJECT_MEMBER
 * en otro caso. La UI deshabilita el dropdown durante la mutación.
 */
function ArtifactOwnerSelect({
  artifact,
  projectId,
  candidates,
}: {
  artifact: Artifact
  projectId: string
  candidates: ProjectMember[]
}) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (newOwnerUserId: string | null) => {
      if (!accessToken) throw new Error('no_access_token')
      return updateArtifactOwner(accessToken, artifact.id, newOwnerUserId)
    },
    onSuccess: () => {
      pushToast({ kind: 'success', message: t('lifecycle.drill_in.owner.toast_success') })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'artifacts'],
      })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('lifecycle.drill_in.owner.toast_failed')
      pushToast({
        kind: 'critical',
        message: code
          ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
          : fallback,
        duration: 6000,
      })
    },
  })

  return (
    <select
      value={artifact.owner_user_id ?? ''}
      onChange={(e) => mutation.mutate(e.target.value === '' ? null : e.target.value)}
      disabled={mutation.isPending}
      aria-label={t('lifecycle.drill_in.owner.select_aria', { name: artifact.name })}
      className="bg-canvas border border-line rounded-md px-2 py-1 text-xs text-ink focus:outline-none focus:border-brand disabled:opacity-50"
    >
      <option value="">{t('lifecycle.drill_in.owner.unassigned_option')}</option>
      {candidates.map((m) =>
        m.user_id ? (
          <option key={m.user_id} value={m.user_id}>
            {m.user_display_name ?? m.user_email ?? '—'}
          </option>
        ) : null,
      )}
    </select>
  )
}


function PhaseOwnerSelect({
  phase,
  projectId,
  internalMembers,
}: {
  phase: ProjectPhase
  projectId: string
  internalMembers: ProjectMember[]
}) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (newOwnerUserId: string | null) => {
      if (!accessToken) throw new Error('no_access_token')
      return updatePhaseOwner(accessToken, projectId, phase.id, newOwnerUserId)
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('team.phase_owner.toast_success'),
      })
      // Invalida el lifecycle para refrescar `owner_user_id` en BD.
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'lifecycle'],
      })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.phase_owner.toast_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  const currentValue = phase.owner_user_id ?? ''
  // Si el owner actual NO está en la lista de members internos (legacy
  // o user fue removido), lo preservamos como una <option> al inicio
  // para no perder la referencia visual.
  const currentInList = internalMembers.some(
    (m) => m.user_id === phase.owner_user_id,
  )
  const phaseName = phase.name || phase.phase_code

  return (
    <label className="flex items-center gap-2 text-ink-muted">
      <span>{t('team.phase_owner.label')}:</span>
      <select
        value={currentValue}
        onChange={(e) => {
          const value = e.target.value
          mutation.mutate(value === '' ? null : value)
        }}
        disabled={mutation.isPending}
        aria-label={t('team.phase_owner.select_aria', { name: phaseName })}
        className="bg-canvas border border-line rounded-md px-2 py-1 text-xs text-ink font-medium focus:outline-none focus:border-brand disabled:opacity-50"
      >
        <option value="">{t('team.phase_owner.default_option')}</option>
        {!currentInList && phase.owner_user_id ? (
          <option value={phase.owner_user_id}>
            {phase.owner_user_id.slice(0, 8)}…
          </option>
        ) : null}
        {internalMembers.map((m) => {
          if (!m.user_id) return null
          const display =
            m.user_display_name ?? m.user_email ?? m.external_email ?? '—'
          return (
            <option key={m.user_id} value={m.user_id}>
              {display}
            </option>
          )
        })}
      </select>
    </label>
  )
}
